import type { Classe } from '@equiview/domain';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import { IconeAlerta, IconeBroto, IconeLista, IconeRelogio, IconeSelo, IconeTabela } from '../components/Icones.js';
import { ChipsDeVisoes } from '../components/VisoesRapidas.js';
import {
  Abas,
  Aguardar,
  Alternador,
  BarraFiltros,
  BarrasHorizontais,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  CampoFiltro,
  CartaoGrafico,
  Etiqueta,
  EstadoVazio,
  FaixaContextoFiltro,
  FaixaIndicadores,
  Indicador,
  Lista,
  Rosca,
  type ColunaLista,
} from '../components/ui/index.js';
import {
  FAIXAS,
  formatarData,
  formatarIdade,
  formatarNascimento,
  hojeIso,
  PELAGENS,
  ROTULO_CLASSE,
  ROTULO_CLASSE_PLURAL,
  ROTULO_FAIXA,
  ROTULO_SEXO,
} from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Haras, Midia } from '../dados/fonte.js';
import {
  descreverFiltros,
  filtrarPlantel,
  filtrosAtivos,
  montarLinhas,
  ordenarPlantel,
  resumirPlantel,
  ROTULO_FILTRO_AVALIACAO,
  ROTULO_MARCHA,
  ROTULO_SITUACAO,
  type FiltroAvaliacao,
  type FiltrosPlantel,
  type LinhaPlantel,
  type Marcha,
  type OrdemPlantel,
  type SituacaoAvaliacao,
} from '../dados/plantel.js';

const CLASSES: Classe[] = ['potro', 'garanhao', 'egua', 'castrado'];
const ORDENS: OrdemPlantel[] = ['nome', 'idade', 'avaliacao', 'avaliacoes'];
const SEXOS: Animal['sex'][] = ['femea', 'macho_inteiro', 'castrado'];
const MARCHAS: Marcha[] = ['batida', 'picada'];
const FILTROS_AVALIACAO: FiltroAvaliacao[] = ['em-dia', 'pendente', 'vencida', 'nunca'];
type Visao = 'lista' | 'cartoes';

/** Plantel: filtros, indicadores, graficos e lista, todos do mesmo recorte. Tudo na URL. */
export function Animais() {
  const consulta = useConsulta(
    async (f) => ({
      haras: await f.haras(),
      animais: await f.listarAnimais(),
      avaliacoes: await f.avaliacoes(),
      capas: await f.capas(),
    }),
    [],
  );
  return (
    <Aguardar consulta={consulta}>
      {({ capas, ...d }) => (
        <Capas.Provider value={capas}>
          <Plantel {...d} />
        </Capas.Provider>
      )}
    </Aguardar>
  );
}

/** Foto de perfil mais recente de cada animal, para a lista e os cartoes. */
const Capas = createContext<Map<string, Midia>>(new Map());

function CapaDoAnimal({ animal, className }: { animal: Animal; className: string }) {
  const capa = useContext(Capas).get(animal.id);
  return <FotoDaMidia midia={capa} className={className} alt={animal.name} />;
}

const CHAVE_VISAO = 'equiview.plantel.visao';
function lerVisao(): Visao {
  try {
    return localStorage.getItem(CHAVE_VISAO) === 'cartoes' ? 'cartoes' : 'lista';
  } catch {
    return 'lista';
  }
}

function Plantel(props: { haras: Haras | null; animais: Animal[]; avaliacoes: Avaliacao[] }) {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  if (!fonte.ficticia && !props.haras) {
    return (
      <div className="space-y-6">
        <CabecalhoPagina titulo="Plantel" meta={<span>Dados deste aparelho.</span>} />
        {equipe ? (
          <EstadoVazio
            titulo="Nenhum haras cadastrado neste aparelho"
            acao={
              <BotaoLink variante="principal" to="/haras/novo">
                Cadastrar o primeiro haras
              </BotaoLink>
            }
          >
            Comece pelo haras; depois cadastre os animais do plantel. Para ver o app com animais de exemplo, ligue os
            dados fictícios.
          </EstadoVazio>
        ) : (
          <EstadoVazio titulo="Nenhum haras liberado para você ainda">
            O plantel aparece aqui assim que a equipe Equiview liberar o seu haras. Se já foi liberado, conecte-se à
            internet para o aparelho receber os dados.
          </EstadoVazio>
        )}
      </div>
    );
  }
  return <ConteudoPlantel {...props} />;
}

/** Le os filtros da URL, descartando valor que nao existe (link antigo ou digitado). */
function lerFiltros(p: URLSearchParams): FiltrosPlantel {
  const um = <T extends string>(lista: readonly T[], v: string | null): T | null => lista.find((x) => x === v) ?? null;
  return {
    classe: um(CLASSES, p.get('classe')),
    faixa: um(FAIXAS, p.get('faixa')),
    q: p.get('q') ?? '',
    sexo: um(SEXOS, p.get('sexo')) ?? '',
    pelagem: p.get('pelagem') ?? '',
    marcha: um(MARCHAS, p.get('marcha')) ?? '',
    avaliacao: um(FILTROS_AVALIACAO, p.get('avaliacao')) ?? '',
  };
}

function ConteudoPlantel({
  haras,
  animais,
  avaliacoes,
}: {
  haras: Haras | null;
  animais: Animal[];
  avaliacoes: Avaliacao[];
}) {
  const [params, setParams] = useSearchParams();
  const f = lerFiltros(params);
  const ordem: OrdemPlantel = ORDENS.find((o) => o === params.get('ordem')) ?? 'nome';
  const direcao = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const [visao, setVisao] = useState<Visao>(lerVisao);
  const hoje = hojeIso();
  const equipe = usePodeCadastrar();
  const fonte = useFonte();

  const linhas = useMemo(() => montarLinhas(animais, avaliacoes, haras, hoje), [animais, avaliacoes, haras, hoje]);
  const chaveFiltros = params.toString();
  // Um recorte so: indicadores, graficos e lista leem `visiveis`.
  const visiveis = useMemo(
    () => ordenarPlantel(filtrarPlantel(linhas, f), ordem, direcao),
    // f, ordem e direcao saem de `params`: a chave da URL basta como dependencia.
    [linhas, chaveFiltros],
  );
  const r = resumirPlantel(visiveis);
  const nomes = useMemo(() => new Map(animais.map((a) => [a.id, a.name])), [animais]);
  const ativos = filtrosAtivos(f);

  const mudar = (novos: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(novos)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true, preventScrollReset: true });
  };
  const limpar = () =>
    mudar({ classe: null, faixa: null, q: null, sexo: null, pelagem: null, marcha: null, avaliacao: null });

  const escolherVisao = (v: Visao) => {
    setVisao(v);
    try {
      localStorage.setItem(CHAVE_VISAO, v);
    } catch {
      // Sem armazenamento: a escolha vale ate recarregar.
    }
  };

  // Contagens das abas e dos graficos: o recorte com os OUTROS filtros, para a
  // escolha de uma fatia nao zerar as demais.
  const semClasse = filtrarPlantel(linhas, f, ['classe', 'faixa']);
  const semAvaliacao = filtrarPlantel(linhas, f, ['avaliacao']);
  const semFaixa = filtrarPlantel(linhas, f, ['faixa']);
  const situacoes = resumirPlantel(semAvaliacao);
  const pelagens = [
    ...new Set([...PELAGENS, ...linhas.map((l) => l.animal.coat_color).filter((c): c is string => !!c)]),
  ].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const pct = (n: number, de: number) => (de > 0 ? `${Math.round((n / de) * 100)}%` : '—');

  const contexto: ReactNode[] = ativos
    ? [
        <span key="n">
          <b>
            {r.total} de {linhas.length}
          </b>{' '}
          animais
        </span>,
        ...descreverFiltros(f),
      ]
    : [];

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        sobretitulo={
          haras ? (
            <span>
              {haras.name}
              {haras.city ? ` · ${haras.city}/${haras.uf}` : ''}
              {fonte.cadastro && (
                <Link to="/haras" className="ml-2 tracking-normal normal-case text-destaque-texto hover:underline">
                  Trocar
                </Link>
              )}
            </span>
          ) : (
            'Meu haras'
          )
        }
        titulo="Plantel"
        meta={<span>Animais cadastrados, com a classe calculada na data de hoje.</span>}
        selo={
          <>
            <span aria-hidden className="size-2 rounded-full bg-destaque" />
            Classes em {formatarData(hoje)}
          </>
        }
        acoes={
          equipe &&
          haras && (
            <>
              <BotaoLink to="/animais/importar">Importar planilha</BotaoLink>
              <BotaoLink variante="principal" to="/animais/novo">
                Novo animal
              </BotaoLink>
            </>
          )
        }
      />

      <ChipsDeVisoes />

      {linhas.length === 0 ? (
        <EstadoVazio
          titulo="O plantel está vazio"
          acao={
            equipe ? (
              <BotaoLink variante="principal" to="/animais/novo">
                Cadastrar o primeiro animal
              </BotaoLink>
            ) : undefined
          }
        >
          Cadastre os animais deste haras. Use “Salvar e cadastrar outro” para ir mais rápido.
        </EstadoVazio>
      ) : (
        <>
          <BarraFiltros
            abas={
              <Abas
                rotulo="Classe"
                valor={f.classe ?? 'todos'}
                onMudar={(v) => mudar({ classe: v === 'todos' ? null : v, faixa: null })}
                abas={[
                  { valor: 'todos', rotulo: 'Todos', contagem: semClasse.length },
                  ...CLASSES.map((c) => ({
                    valor: c,
                    rotulo: ROTULO_CLASSE_PLURAL[c],
                    contagem: semClasse.filter((l) => l.classe === c).length,
                  })),
                ]}
              />
            }
            busca={{
              valor: f.q,
              onMudar: (q) => mudar({ q: q || null }),
              rotulo: 'Buscar por nome ou registro',
              placeholder: 'Buscar nome ou registro',
            }}
            ativos={ativos}
            onLimpar={limpar}
            contagem={
              <>
                <b>{visiveis.length}</b> {visiveis.length === 1 ? 'animal' : 'animais'}
                {ativos > 0 && ` de ${linhas.length}`}
              </>
            }
            extra={
              <div className="hidden md:block">
                <Alternador
                  rotulo="Visualização"
                  valor={visao}
                  onMudar={escolherVisao}
                  opcoes={[
                    { valor: 'lista', titulo: 'Ver em lista', rotulo: <IconeTabela width={16} height={16} /> },
                    {
                      valor: 'cartoes',
                      titulo: 'Ver em cartões com foto',
                      rotulo: <IconeLista width={16} height={16} />,
                    },
                  ]}
                />
              </div>
            }
            campos={
              <>
                <CampoFiltro
                  rotulo="Sexo"
                  todos="Todos"
                  valor={f.sexo}
                  onMudar={(v) => mudar({ sexo: v || null })}
                  opcoes={SEXOS.map((s) => ({ valor: s, rotulo: ROTULO_SEXO[s] }))}
                />
                <CampoFiltro
                  rotulo="Faixa do potro"
                  todos="Todas as idades"
                  valor={f.faixa ?? ''}
                  // Faixa so existe em potro: escolher uma ja filtra a classe.
                  onMudar={(v) => mudar({ faixa: v || null, classe: v ? 'potro' : f.classe })}
                  opcoes={FAIXAS.map((x) => ({ valor: x, rotulo: ROTULO_FAIXA[x] }))}
                />
                <CampoFiltro
                  rotulo="Pelagem"
                  todos="Todas"
                  valor={f.pelagem}
                  onMudar={(v) => mudar({ pelagem: v || null })}
                  opcoes={pelagens.map((p) => ({ valor: p, rotulo: p }))}
                />
                <CampoFiltro
                  rotulo="Marcha declarada"
                  todos="Todas"
                  valor={f.marcha}
                  onMudar={(v) => mudar({ marcha: v || null })}
                  opcoes={MARCHAS.map((m) => ({ valor: m, rotulo: ROTULO_MARCHA[m] }))}
                />
                <CampoFiltro
                  rotulo="Avaliação"
                  todos="Qualquer situação"
                  valor={f.avaliacao}
                  onMudar={(v) => mudar({ avaliacao: v || null })}
                  opcoes={FILTROS_AVALIACAO.map((a) => ({ valor: a, rotulo: ROTULO_FILTRO_AVALIACAO[a] }))}
                />
              </>
            }
          />

          <FaixaIndicadores rotulo="Resumo do plantel">
            <Indicador
              destaque
              rotulo={ativos ? 'Animais no recorte' : 'Animais no plantel'}
              valor={r.total}
              icone={<IconeLista width={17} height={17} />}
              complemento={
                ativos
                  ? `de ${linhas.length} no plantel`
                  : `${r.porClasse.garanhao} garanhões · ${r.porClasse.egua} éguas · ${r.porClasse.castrado} castrados`
              }
            />
            <Indicador
              rotulo="Avaliados em 12 meses"
              valor={r.emDia}
              icone={<IconeSelo width={17} height={17} />}
              complemento={`${pct(r.emDia, r.total)} do recorte`}
            />
            <Indicador
              rotulo="Sem avaliação no ano"
              valor={r.vencida + r.nunca}
              icone={<IconeAlerta width={17} height={17} />}
              complemento={`${r.nunca} nunca avaliado${r.nunca === 1 ? '' : 's'}`}
            />
            <Indicador
              rotulo="Potros"
              valor={r.potros}
              icone={<IconeBroto width={17} height={17} />}
              complemento="menos de 3 anos, sem filho"
            />
            <Indicador
              rotulo="Última avaliação"
              valor={r.ultimaAvaliacao ? formatarData(r.ultimaAvaliacao) : '—'}
              icone={<IconeRelogio width={17} height={17} />}
              complemento={ativos ? 'no recorte' : 'no plantel'}
            />
          </FaixaIndicadores>

          <FaixaContextoFiltro partes={contexto} />

          <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            {f.classe === 'potro' ? (
              <CartaoGrafico
                titulo="Potros por faixa de idade"
                sub="Clique numa faixa para filtrar. Idade calculada na data de hoje."
              >
                <BarrasHorizontais
                  rotulo="Potros por faixa de idade"
                  formatar={String}
                  itens={FAIXAS.map((x) => {
                    const n = semFaixa.filter((l) => l.faixa === x).length;
                    return { chave: x, rotulo: ROTULO_FAIXA[x], valor: n, sub: pct(n, semFaixa.length) };
                  })}
                  ativo={f.faixa}
                  onEscolher={(x) => mudar({ faixa: f.faixa === x ? null : x })}
                  vazio="Nenhum potro no recorte."
                />
              </CartaoGrafico>
            ) : (
              <CartaoGrafico
                titulo="Composição por classe"
                sub="Clique numa classe para filtrar. Classe na data de hoje."
              >
                <BarrasHorizontais
                  rotulo="Animais por classe"
                  formatar={String}
                  itens={CLASSES.map((c) => {
                    const n = semClasse.filter((l) => l.classe === c).length;
                    return { chave: c, rotulo: ROTULO_CLASSE_PLURAL[c], valor: n, sub: pct(n, semClasse.length) };
                  })}
                  ativo={f.classe}
                  onEscolher={(c) => mudar({ classe: f.classe === c ? null : c, faixa: null })}
                  vazio="Nenhum animal no recorte."
                />
              </CartaoGrafico>
            )}
            <CartaoGrafico
              titulo="Situação de avaliação"
              sub="Em dia: avaliado nos últimos 12 meses. Clique para filtrar."
            >
              <Rosca
                rotulo="Situação de avaliação"
                unidade={situacoes.total === 1 ? 'animal' : 'animais'}
                foco={f.avaliacao && f.avaliacao !== 'pendente' ? f.avaliacao : null}
                onEscolher={(s) => mudar({ avaliacao: f.avaliacao === s ? null : s })}
                fatias={(
                  [
                    ['em-dia', situacoes.emDia, 1],
                    ['vencida', situacoes.vencida, 2],
                    ['nunca', situacoes.nunca, 3],
                  ] as const
                ).map(([chave, valor, passo]: readonly [SituacaoAvaliacao, number, 1 | 2 | 3]) => ({
                  chave,
                  rotulo: ROTULO_SITUACAO[chave],
                  valor,
                  passo,
                }))}
                vazio="Nenhum animal no recorte."
              />
            </CartaoGrafico>
          </div>

          <section aria-label="Animais" className="space-y-4">
            {visao === 'cartoes' ? (
              <>
                <div className="md:hidden">
                  <ListaDoPlantel
                    linhas={visiveis}
                    ordem={ordem}
                    direcao={direcao}
                    mudar={mudar}
                    nomes={nomes}
                    limpar={limpar}
                    chave={chaveFiltros}
                  />
                </div>
                <div className="hidden md:block">
                  {visiveis.length ? <Cartoes linhas={visiveis} /> : <SemResultado limpar={limpar} />}
                </div>
              </>
            ) : (
              <ListaDoPlantel
                linhas={visiveis}
                ordem={ordem}
                direcao={direcao}
                mudar={mudar}
                nomes={nomes}
                limpar={limpar}
                chave={chaveFiltros}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function SemResultado({ limpar }: { limpar: () => void }) {
  return (
    <EstadoVazio
      titulo="Nenhum animal encontrado"
      acao={
        <Botao tamanho="sm" onClick={limpar}>
          Limpar filtros
        </Botao>
      }
    >
      Nenhum animal do plantel corresponde a esse filtro ou busca.
    </EstadoVazio>
  );
}

function ClasseCelula({ l }: { l: LinhaPlantel }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Etiqueta tom={l.classe === 'potro' ? 'destaque' : 'neutro'}>{ROTULO_CLASSE[l.classe]}</Etiqueta>
      {l.faixa && <span className="numero text-xs text-apagado">{ROTULO_FAIXA[l.faixa]}</span>}
    </span>
  );
}

const idadeDaLinha = (l: LinhaPlantel) => `${l.animal.birth_date_estimated ? '≈ ' : ''}${formatarIdade(l.meses)}`;

const COLUNAS: ColunaLista<LinhaPlantel>[] = [
  { id: 'idade', rotulo: 'Idade', largura: '140px', celula: idadeDaLinha, medida: (l) => l.meses, dirPadrao: 'asc' },
  { id: 'avaliacoes', rotulo: 'Fichas', largura: '84px', celula: (l) => l.avaliacoes, medida: (l) => l.avaliacoes },
  {
    id: 'avaliacao',
    rotulo: 'Última avaliação',
    rotuloCurto: 'Avaliado em',
    largura: '128px',
    celula: (l) => (l.ultimaAvaliacao ? formatarData(l.ultimaAvaliacao) : '—'),
    dirPadrao: 'desc',
  },
];

function ListaDoPlantel({
  linhas,
  ordem,
  direcao,
  mudar,
  nomes,
  limpar,
  chave,
}: {
  linhas: LinhaPlantel[];
  ordem: OrdemPlantel;
  direcao: 'asc' | 'desc';
  mudar: (n: Record<string, string | null>) => void;
  nomes: Map<string, string>;
  limpar: () => void;
  /** Recorte atual: muda a `key` e a janela volta ao inicio. */
  chave: string;
}) {
  return (
    <Lista<LinhaPlantel>
      key={chave}
      titulo="Animais"
      dica="Abra uma linha para o resumo · linha apagada: sem avaliação completa (só censo ou nenhuma)"
      linhas={linhas}
      chave={(l) => l.animal.id}
      rotuloItem={(l) => l.animal.name}
      rotuloPrincipal="Animal"
      ordemPrincipal={{ id: 'nome', rotulo: 'Nome' }}
      colunas={COLUNAS}
      ordem={{ col: ordem, dir: direcao }}
      onOrdenar={(o) => mudar({ ordem: o.col === 'nome' ? null : o.col, dir: o.dir === 'desc' ? 'desc' : null })}
      fraca={(l) => l.nivelMaximo < 2}
      vazio={
        <span className="space-y-3">
          <span className="block">Nenhum animal do plantel corresponde a esse filtro ou busca.</span>
          <Botao tamanho="sm" onClick={limpar}>
            Limpar filtros
          </Botao>
        </span>
      }
      principal={(l) => (
        <div className="flex items-center gap-3">
          <CapaDoAnimal animal={l.animal} className="size-11 shrink-0 rounded-[10px]" />
          <div className="min-w-0">
            <Link
              to={`/animais/${l.animal.id}`}
              className="alvo-toque line-clamp-2 font-serif text-[15px] leading-snug font-semibold text-marca hover:underline"
            >
              {l.animal.name}
            </Link>
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-apagado">
              {l.animal.real && <Etiqueta tom="ok">Dados reais</Etiqueta>}
              <ClasseCelula l={l} />
              <span aria-hidden className="opacity-50">
                ·
              </span>
              <span className="font-mono text-[11.5px]">{l.animal.abccmm_registry ?? 'Sem registro'}</span>
              {l.animal.coat_color && (
                <>
                  <span aria-hidden className="opacity-50">
                    ·
                  </span>
                  {l.animal.coat_color}
                </>
              )}
            </p>
          </div>
        </div>
      )}
      detalhe={(l) => <DetalheDoAnimal l={l} nomes={nomes} />}
    />
  );
}

function DetalheDoAnimal({ l, nomes }: { l: LinhaPlantel; nomes: Map<string, string> }) {
  const a = l.animal;
  const nome = (id: string | null) => (id ? (nomes.get(id) ?? 'Fora da base') : '—');
  return (
    <div className="space-y-4">
      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <GrupoDetalhe titulo="Identificação">
          <LinhaDetalhe rotulo="Registro">{a.abccmm_registry ?? '—'}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Sexo">{ROTULO_SEXO[a.sex]}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Nascimento">{formatarNascimento(a)}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Pelagem">{a.coat_color ?? '—'}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Marcha declarada">
            {a.marcha_type_declared ? ROTULO_MARCHA[a.marcha_type_declared] : '—'}
          </LinhaDetalhe>
        </GrupoDetalhe>
        <GrupoDetalhe titulo="Genealogia">
          <LinhaDetalhe rotulo="Pai">{nome(a.sire_id)}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Mãe">{nome(a.dam_id)}</LinhaDetalhe>
        </GrupoDetalhe>
        <GrupoDetalhe titulo="Avaliações">
          <LinhaDetalhe rotulo="Fichas">{l.avaliacoes}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Situação">{ROTULO_SITUACAO[l.situacao]}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Primeira">{l.primeiraAvaliacao ? formatarData(l.primeiraAvaliacao) : '—'}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Última">{l.ultimaAvaliacao ? formatarData(l.ultimaAvaliacao) : '—'}</LinhaDetalhe>
          <LinhaDetalhe rotulo="Nível mais completo">
            {l.nivelMaximo === 0 ? '—' : l.nivelMaximo === 1 ? 'Só censo (nível 1)' : `Nível ${l.nivelMaximo}`}
          </LinhaDetalhe>
        </GrupoDetalhe>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-borda pt-3.5">
        <BotaoLink tamanho="sm" variante="principal" to={`/animais/${a.id}`}>
          Abrir a ficha
        </BotaoLink>
        <BotaoLink tamanho="sm" to={`/comparar?a=${a.id}`}>
          Comparar
        </BotaoLink>
        {l.avaliacoes > 0 && (
          <BotaoLink tamanho="sm" to={`/animais/${a.id}/laudo`}>
            Laudo
          </BotaoLink>
        )}
      </div>
    </div>
  );
}

function GrupoDetalhe({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-2 text-[10.5px] font-bold tracking-[0.1em] text-destaque-texto uppercase">{titulo}</h3>
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function LinhaDetalhe({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-[13px]">
      <dt className="text-apagado">{rotulo}</dt>
      <dd className="numero min-w-0 truncate text-right font-semibold">{children}</dd>
    </div>
  );
}

function Cartoes({ linhas }: { linhas: LinhaPlantel[] }) {
  return (
    <ul className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
      {linhas.map((l) => (
        <li key={l.animal.id}>
          <Link
            to={`/animais/${l.animal.id}`}
            className="group block overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie shadow-cartao transition-colors hover:border-destaque"
          >
            <CapaDoAnimal animal={l.animal} className="aspect-3/2 w-full" />
            <div className="space-y-2 border-t border-borda p-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <ClasseCelula l={l} />
                {l.animal.real && <Etiqueta tom="ok">Dados reais</Etiqueta>}
              </div>
              <h2 className="font-serif text-lg leading-snug font-semibold text-marca group-hover:underline">
                {l.animal.name}
              </h2>
              <dl className="grid grid-cols-2 gap-x-3 text-[13px]">
                <dt className="text-apagado">Registro</dt>
                <dd className="text-right font-mono">{l.animal.abccmm_registry ?? '—'}</dd>
                <dt className="text-apagado">Idade</dt>
                <dd className="numero text-right">{idadeDaLinha(l)}</dd>
                <dt className="text-apagado">Avaliação</dt>
                <dd className="numero text-right">{l.ultimaAvaliacao ? formatarData(l.ultimaAvaliacao) : '—'}</dd>
              </dl>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
